import { useContext, useMemo } from 'react';
import { MdOutlineDone } from 'react-icons/md';
import { RxCross2 } from 'react-icons/rx';
import { getLocalTimeZone, parseZonedDateTime } from '@internationalized/date';
import { TmdxAvatar } from '@ds';
import { AuthContext } from '@lp/core/auth';
import { LogisticsLogRecord } from '@lp/features/RoutePlanner/types';
import { useQuoteDetails } from '@lp/features/RoutePlanner/hooks/useQuoteDetails';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { getCaseLogUserName } from '@features/components/CaseOverview/components/CaseLogTab/utils';
import { CaseLogEventType, QuoteStatus, RoutePlanStatus } from '@/api/apis/case';
import { formatDate, getTzDateTimeString } from '@/utils/helpers';
import { CaseDetailsPermissions } from '@/permissions';
import { EmployeeRole } from '@/api/apis/resources';

const EVENT_TYPE_STATUS_TEXT: Partial<Record<CaseLogEventType, string>> = {
  [CaseLogEventType.CaseQuoteSentToTxCenter]: 'Quote sent to TX Center',
  [CaseLogEventType.CaseQuoteApprovedTxCenterProvider]: 'TX Center provider approved',
};

const LOG_TYPE_STATUS_TEXT: Partial<Record<string, Record<string, string>>> = {
  Aviation: {
    Approved: 'Aviation approved',
    Rejected: 'Aviation rejected',
  },
  Ground: {
    Approved: 'Ground approved',
    Rejected: 'Ground rejected',
  },
  Quote: {
    Approved: 'Quote approved internally',
    Rejected: 'Quote declined internally',
    Recalled: 'Quote recalled',
  },
  TXCenterQuote: {
    Approved: 'Quote approved by TX Center',
    Rejected: 'Quote declined by TX Center',
  },
};

const ROUTE_STATUS_TEXT: Partial<Record<string, string>> = {
  [RoutePlanStatus.RoutePlanningStart]: 'Route planning started',
  [RoutePlanStatus.WaitingForAviationApproval]: 'Send for aviation approval',
  [RoutePlanStatus.WaitingForQuoteApproval]: 'Sent for Top 3 approval',
  [RoutePlanStatus.RoutePlanningDone]: 'Route planning done',
  [RoutePlanStatus.PendingResourcesBooking]: 'Resources assignment sent',
  [RoutePlanStatus.ResourcesBooked]: 'Resources are assigned',
};

export type TimelineItemProps = {
  isPositive: boolean;
  eventType: CaseLogEventType;
  caseLog: LogisticsLogRecord;
  user?: {
    userId: string;
    firstName: string;
    lastName: string;
    profilePhoto: string;
    roles: EmployeeRole[] | null;
  };
  isLast: boolean;
  isFirst: boolean;
  createdAt: string;
};

export const TimelineItem = ({
  isPositive,
  eventType,
  caseLog,
  user,
  isLast,
  isFirst,
  createdAt,
}: TimelineItemProps) => {
  const { quote } = useCaseDetailsWithReviews();
  const { openQuote } = useQuoteDetails();
  const { checkPermission } = useContext(AuthContext);

  const lineColor = '#31C48D';
  const acceptIconColor = '#0E9F6E';
  const declineIconColor = '#F05252';

  const circleColor = isPositive ? acceptIconColor : declineIconColor;
  const coloredLastLine = isLast ? 'transparent' : lineColor;

  const { userName, fullName } = getCaseLogUserName(user);
  const createdAtString = useMemo(() => {
    const dateTimeString = getTzDateTimeString(createdAt);
    const utcString = parseZonedDateTime(dateTimeString).toAbsoluteString();

    return formatDate(new Date(utcString), 'MMM dd, yyyy HH:mm zz', { timezone: getLocalTimeZone() });
  }, [createdAt]);

  const eventText = useMemo(() => {
    const statusTextByEvent = EVENT_TYPE_STATUS_TEXT[eventType];
    const statusTextByRoute =
      eventType === CaseLogEventType.RoutePlanningStatusUpdated && ROUTE_STATUS_TEXT[caseLog.status];

    if (eventType === CaseLogEventType.CaseReviewUpdated) {
      return LOG_TYPE_STATUS_TEXT[caseLog.type]?.[caseLog.status] || '';
    }

    return statusTextByEvent || statusTextByRoute;
  }, [eventType, caseLog]);

  const showQuoteLink = useMemo(() => {
    const hasPermission = checkPermission(CaseDetailsPermissions.CaseQuoteRead);
    const hasQuote = quote?.status && quote?.status !== QuoteStatus.NoQuote;

    return hasPermission && hasQuote && eventType === CaseLogEventType.CaseQuoteRejectedByTxCenter;
  }, [checkPermission, eventType, quote]);

  return (
    <div className="flex gap-4 items-stretch">
      <div className="pl-[5px] flex flex-col relative">
        <div className="w-1 h-1/2 bg-danger-10" style={{ backgroundColor: isFirst ? 'transparent' : lineColor }} />
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-[5px] z-2 flex justify-center items-center w-[14px] h-[14px] bg-white border-3 rounded-full"
          style={{ borderColor: circleColor, backgroundColor: circleColor }}
        >
          {isPositive ? <MdOutlineDone color="white" size={10} /> : <RxCross2 color="white" size={10} />}
        </div>
        <div className="w-1 h-1/2 bg-danger-10" style={{ backgroundColor: coloredLastLine }} />
      </div>
      <div className="py-2">
        <div className={`p-4 ${!isPositive ? 'bg-danger-90' : 'bg-base-80'} rounded-md w-[460px]`}>
          <div className="flex flex-row justify-between mb-1">
            <p className="text-m-sb">{eventText}</p>
            {showQuoteLink && (
              <button
                type="button"
                className="border-b-1 border-base-5 text-m-sb text-base-5"
                onClick={() => openQuote({ rejectedByTxCenter: true, isViewOnly: true })}
              >
                View quote
              </button>
            )}
          </div>
          {user && (
            <div className="flex flex-row items-center gap-1">
              <TmdxAvatar size="sm" radius="sm" name={fullName} src={user.profilePhoto} />
              <p className="text-xs">{userName}</p>
              <div className="h-1 w-1 bg-base-10 rounded-full" />
              <p className="text-s-r text-base-20 text-xs">{createdAtString}</p>
            </div>
          )}
          {!!caseLog.comment && (
            <span className="w-fit mt-2 flex p-2 text-s-r bg-base-90 text-base-5 rounded-lg">{`"${caseLog.comment}"`}</span>
          )}
        </div>
      </div>
    </div>
  );
};
